import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Select from 'react-select'
import Swal from 'sweetalert2'
import ProjectAddTrainerModal from './ProjectAddTrainerModal'
import ProjectTrainerExtraCosts from './ProjectTrainerExtraCosts'
import {
	applyRecalculatedDoWyplaty,
	buildTrainerOptionRows,
	calculateDistanceKmBetweenCoords,
	calculateDoWyplatyDojazdy,
	calculateDoWyplatyGodziny,
	calculateDoWyplatyInne,
	createTrainerExtraCost,
	formatMoneyPln,
	formatTrainerDistanceKm,
	getStepLocationCoords,
	normalizeTrainerExtraCosts,
	sortTrainerOptions,
} from './projectTrainerHelpers'
import { normalizeCertificates } from '../common/normalizeCertificates'
import { clonePozycja, createEmptyPozycja, normalizePozycje } from '../common/normalizePozycje'
import { fetchGoogleDrivingDistanceKm } from './googleDrivingDistance'
import AvailabilityService from '@/services/AvailabilityService'
import UserService2 from '@/services/UserService2'
import {
	DEFAULT_HOURLY_RATE_TYPE,
	HOURLY_RATE_TYPES,
	normalizeTypStawkiGodzinowej,
	resolveHourlyRateByType,
} from '@/shared/hourlyRateTypes'

const relationId = value => {
	if (!value) return ''
	if (typeof value === 'object') return `${value._id || value.id || ''}`
	return `${value}`
}

const stepDateKey = value => {
	const match = `${value || ''}`.match(/^(\d{4}-\d{2}-\d{2})/)
	return match ? match[1] : ''
}

const buildAvailabilityByTrainerId = rows => {
	const map = {}
	;(Array.isArray(rows) ? rows : []).forEach(item => {
		const trainerId = relationId(item?.userId || item?.trainerId)
		if (!trainerId) return
		const morning = item.morning === true || (Array.isArray(item.slots) && item.slots.includes('8-14'))
		const afternoon = item.afternoon === true || (Array.isArray(item.slots) && item.slots.includes('14-20'))
		const current = map[trainerId] || { morning: false, afternoon: false }
		map[trainerId] = {
			morning: current.morning || morning,
			afternoon: current.afternoon || afternoon,
		}
	})
	return map
}

const resolveTrainerAvailability = (entry, lookup) => {
	if (lookup !== 'ready') return { status: lookup }
	if (!entry?.morning && !entry?.afternoon) return { status: 'unavailable' }
	if (entry.morning && entry.afternoon) return { status: 'available' }
	if (entry.morning) return { status: 'morning' }
	return { status: 'afternoon' }
}

const inputClassName = 'block w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500'
const readOnlyValueClassName = 'flex min-h-[42px] items-center rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-medium text-gray-900'

const withComputedHours = pozycja => {
	const forDuration = {
		...pozycja,
		end_time: '',
	}
	return applyRecalculatedDoWyplaty(pozycja, { content: [forDuration] })
}

export default function ProjectStepPositionsSection({
	form,
	setField,
	previousStep = null,
	certificationOptions = [],
}) {
	const { t } = useTranslation()
	const [activePozycjaIndex, setActivePozycjaIndex] = useState(null)
	const [loadingTrainerOptions, setLoadingTrainerOptions] = useState(false)
	const [modalTrainerSource, setModalTrainerSource] = useState([])
	const [isAddTrainerModalOpen, setIsAddTrainerModalOpen] = useState(false)
	const [trainerSearchTerm, setTrainerSearchTerm] = useState('')
	const [trainerSortColumn, setTrainerSortColumn] = useState('distanceKm')
	const [trainerSortDirection, setTrainerSortDirection] = useState('asc')
	const [trainerOptionsLimit, setTrainerOptionsLimit] = useState(5)
	const [selectingTrainer, setSelectingTrainer] = useState(false)
	const [trainerAvailabilityById, setTrainerAvailabilityById] = useState({})
	const [trainerAvailabilityLookup, setTrainerAvailabilityLookup] = useState('unknown')

	const pozycje = normalizePozycje(form.pozycje, { keepEmpty: true })

	const hourlyRateTypeOptions = useMemo(
		() => HOURLY_RATE_TYPES.map(item => ({
			value: item.code,
			label: t(item.labelKey, { defaultValue: item.defaultLabel }),
		})),
		[t]
	)

	const activePozycjaHourlyRateType = normalizeTypStawkiGodzinowej(
		activePozycjaIndex != null
			? pozycje[activePozycjaIndex]?.typ_stawki_godzinowej
			: DEFAULT_HOURLY_RATE_TYPE
	)

	const certificationMap = useMemo(
		() => new Map(
			(Array.isArray(certificationOptions) ? certificationOptions : []).map(item => [
				`${item.value}`,
				item.label || '—',
			])
		),
		[certificationOptions]
	)

	const updatePozycje = useCallback(next => {
		setField('pozycje', next)
	}, [setField])

	const getPozycjaRequiredCertificationIds = pozycja => (
		normalizeCertificates(pozycja?.certificates).map(item => item.certificateId)
	)

	const getSelectedCertificationOptions = pozycja => {
		const selectedCertificateIds = getPozycjaRequiredCertificationIds(pozycja)
		return selectedCertificateIds.map(id => {
			const fromOptions = certificationOptions.find(option => `${option.value}` === id)
			if (fromOptions) {
				return {
					value: `${fromOptions.value}`,
					label: fromOptions.label || '—',
				}
			}
			const stored = normalizeCertificates(pozycja?.certificates)
				.find(item => `${item.certificateId}` === id)
			return {
				value: id,
				label: `${stored?.certificateName || ''}`.trim() || '—',
			}
		})
	}

	const addPozycja = index => {
		const source = Number.isInteger(index) ? pozycje[index] : pozycje[pozycje.length - 1]
		const copy = source ? withComputedHours(clonePozycja(source)) : createEmptyPozycja()
		if (!Number.isInteger(index)) {
			updatePozycje([...pozycje, copy])
			return
		}
		const next = [...pozycje]
		next.splice(index + 1, 0, copy)
		updatePozycje(next)
	}

	const removePozycja = index => {
		updatePozycje(pozycje.filter((_, itemIndex) => itemIndex !== index))
	}

	const updatePozycja = (index, updates, { recalculate = true } = {}) => {
		const next = pozycje.map((item, itemIndex) => {
			if (itemIndex !== index) return item
			const merged = { ...item, ...updates }
			return recalculate ? withComputedHours(merged) : merged
		})
		updatePozycje(next)
	}

	const hasCoordinates = form?.g_lat !== '' && form?.g_lat != null
		&& form?.g_long !== '' && form?.g_long != null

	const previousStepLocation = useMemo(
		() => getStepLocationCoords(previousStep),
		[previousStep]
	)

	const distanceFromPreviousPlacowkaKm = useMemo(() => {
		if (!previousStepLocation || !hasCoordinates) return null
		return calculateDistanceKmBetweenCoords(
			previousStepLocation.g_lat,
			previousStepLocation.g_long,
			form.g_lat,
			form.g_long
		)
	}, [form.g_lat, form.g_long, hasCoordinates, previousStepLocation])

	const openTrainerModal = async index => {
		if (!hasCoordinates) {
			await Swal.fire(
				t('notifications.errorTitle'),
				t('projects.details.messages.trainingLocationRequired'),
				'error'
			)
			return
		}

		setLoadingTrainerOptions(true)
		setActivePozycjaIndex(index)
		const requiredCertificationIds = getPozycjaRequiredCertificationIds(pozycje[index])
		const stepDate = stepDateKey(form.data_rozpoczecia)
		const [result, availabilityResult] = await Promise.all([
			UserService2.getTrainersForTraining({
				g_lat: form.g_lat,
				g_long: form.g_long,
				requiredCertificationIds,
			}),
			stepDate
				? AvailabilityService.getWithFilters({
					limit: 2000,
					sortColumn: 'startDate',
					sortDirection: 'asc',
					filters: { onDate: stepDate },
				})
				: Promise.resolve(null),
		])
		setLoadingTrainerOptions(false)

		if (result.error) {
			await Swal.fire(t('notifications.errorTitle'), result.error, 'error')
			return
		}

		if (!stepDate) {
			setTrainerAvailabilityLookup('noDate')
			setTrainerAvailabilityById({})
		} else if (!availabilityResult || availabilityResult.error) {
			setTrainerAvailabilityLookup('unknown')
			setTrainerAvailabilityById({})
		} else {
			setTrainerAvailabilityLookup('ready')
			setTrainerAvailabilityById(buildAvailabilityByTrainerId(availabilityResult.data))
		}

		setModalTrainerSource(Array.isArray(result.data) ? result.data : [])
		setTrainerSearchTerm('')
		setTrainerOptionsLimit(5)
		setIsAddTrainerModalOpen(true)
	}

	const activePozycjaRequiredCertificationIds = useMemo(() => {
		if (activePozycjaIndex == null) return []
		return getPozycjaRequiredCertificationIds(pozycje[activePozycjaIndex])
	}, [activePozycjaIndex, pozycje])

	const getMissingCertificationNames = useCallback((trainerItem, requiredCertificationIds) => {
		const normalizedRequiredCertificationIds = Array.isArray(requiredCertificationIds)
			? requiredCertificationIds.filter(Boolean).map(id => `${id}`)
			: []
		if (!normalizedRequiredCertificationIds.length) return []

		const trainerCertificateIds = new Set(
			(Array.isArray(trainerItem?.certificates) ? trainerItem.certificates : [])
				.map(item => `${item?.certificateId || item?._id || item || ''}`.trim())
				.filter(Boolean)
		)

		return normalizedRequiredCertificationIds
			.filter(id => !trainerCertificateIds.has(id))
			.map(id => certificationMap.get(id) || id)
	}, [certificationMap])

	const trainerOptions = useMemo(() => buildTrainerOptionRows({
		availableTrainers: modalTrainerSource,
		certificationMap,
		getMissingCertificationNames,
		requiredCertificationIds: activePozycjaRequiredCertificationIds,
		hourlyRateType: activePozycjaHourlyRateType,
	}).map(option => ({
		...option,
		availability: resolveTrainerAvailability(
			trainerAvailabilityById[relationId(option.value)],
			trainerAvailabilityLookup,
		),
	})), [
		activePozycjaHourlyRateType,
		activePozycjaRequiredCertificationIds,
		certificationMap,
		getMissingCertificationNames,
		modalTrainerSource,
		trainerAvailabilityById,
		trainerAvailabilityLookup,
	])

	const filteredTrainerOptions = useMemo(() => {
		const query = `${trainerSearchTerm || ''}`.trim().toLowerCase()
		if (!query) return trainerOptions
		return trainerOptions.filter(option => {
			const haystack = [
				option.trainerName,
				option.miasto,
				...(Array.isArray(option.certificationNames) ? option.certificationNames : []),
			].filter(Boolean).join(' ').toLowerCase()
			return haystack.includes(query)
		})
	}, [trainerOptions, trainerSearchTerm])

	const sortedTrainerOptions = useMemo(
		() => sortTrainerOptions(filteredTrainerOptions, trainerSortColumn, trainerSortDirection),
		[filteredTrainerOptions, trainerSortColumn, trainerSortDirection]
	)

	const visibleTrainerOptions = useMemo(
		() => sortedTrainerOptions.slice(0, trainerOptionsLimit),
		[sortedTrainerOptions, trainerOptionsLimit]
	)

	const handleHourlyRateTypeChange = async (index, option) => {
		const nextType = normalizeTypStawkiGodzinowej(option?.value)
		const pozycja = pozycje[index]
		const trainerId = relationId(pozycja?.trainerId)
		const updates = { typ_stawki_godzinowej: nextType }

		if (trainerId) {
			const fromModal = modalTrainerSource.find(item => relationId(item._id || item.id) === trainerId)
			if (fromModal) {
				updates.stawka_godzinowa = resolveHourlyRateByType(fromModal, nextType)
			} else {
				const trainerResult = await UserService2.getById(trainerId)
				if (!trainerResult.error && trainerResult.data) {
					updates.stawka_godzinowa = resolveHourlyRateByType(trainerResult.data, nextType)
				}
			}
		}

		updatePozycja(index, updates)
	}

	const handleChooseTrainer = async trainer => {
		if (activePozycjaIndex == null || selectingTrainer) return
		const targetIndex = activePozycjaIndex
		const rateType = normalizeTypStawkiGodzinowej(
			pozycje[targetIndex]?.typ_stawki_godzinowej || DEFAULT_HOURLY_RATE_TYPE
		)
		const distanceFromHomeKm = trainer.distanceKm ?? ''
		const parsedHomeDistance = Number(distanceFromHomeKm)
		const parsedPreviousDistance = Number(distanceFromPreviousPlacowkaKm)
		const hasHomeDistance = Number.isFinite(parsedHomeDistance)
		const hasPreviousDistance = Number.isFinite(parsedPreviousDistance)
		const preferredDistanceKm = hasPreviousDistance && (
			!hasHomeDistance || parsedPreviousDistance < parsedHomeDistance
		)
			? parsedPreviousDistance
			: distanceFromHomeKm

		setSelectingTrainer(true)
		let googleRoadDistanceKm = ''
		try {
			googleRoadDistanceKm = await fetchGoogleDrivingDistanceKm(
				trainer.g_lat,
				trainer.g_long,
				form.g_lat,
				form.g_long
			) ?? ''
		} catch (error) {
			console.error('Failed to fetch Google driving distance:', error)
			googleRoadDistanceKm = ''
		} finally {
			setSelectingTrainer(false)
		}

		updatePozycja(targetIndex, {
			trainerId: trainer.trainerId || null,
			trainerName: trainer.trainerName || '',
			miasto: trainer.miasto || '',
			odleglosc_km: preferredDistanceKm,
			odleglosc_z_domu_km: distanceFromHomeKm,
			odleglosc_z_poprzedniej_placowki_km: distanceFromPreviousPlacowkaKm ?? '',
			odleglosc_drogi_google_km: googleRoadDistanceKm,
			typ_stawki_godzinowej: rateType,
			stawka_godzinowa: resolveHourlyRateByType(trainer, rateType),
			stawka_za_kilometr: 1,
			rozliczono: false,
		})
		setIsAddTrainerModalOpen(false)
		setActivePozycjaIndex(null)
	}

	return (
		<div className="rounded-lg border border-gray-200 p-4">
			<div>
				<h3 className="text-sm font-semibold text-gray-900">
					{t('projects.form.positionsSection.title', { defaultValue: 'Pozycje etapu' })}
				</h3>
				<p className="mt-1 text-sm text-gray-500">
					{t('projects.form.positionsSection.description', {
						defaultValue: 'Każda pozycja łączy trenera, zawartość i rozliczenie.',
					})}
				</p>
			</div>

			<div className="mt-4 space-y-4">
				{pozycje.length ? pozycje.map((pozycja, index) => {
					const doWyplatyGodziny = calculateDoWyplatyGodziny(pozycja, { content: [pozycja] })
					const doWyplatyDojazdy = calculateDoWyplatyDojazdy(pozycja)
					const doWyplatyInne = calculateDoWyplatyInne(pozycja)
					const distanceFromHomeLabel = formatTrainerDistanceKm(
						pozycja.odleglosc_z_domu_km !== '' && pozycja.odleglosc_z_domu_km != null
							? pozycja.odleglosc_z_domu_km
							: (pozycja.trainerId ? pozycja.odleglosc_km : null)
					)
					const distanceFromPreviousLabel = formatTrainerDistanceKm(
						pozycja.odleglosc_z_poprzedniej_placowki_km !== ''
							&& pozycja.odleglosc_z_poprzedniej_placowki_km != null
							? pozycja.odleglosc_z_poprzedniej_placowki_km
							: (pozycja.trainerId ? distanceFromPreviousPlacowkaKm : null)
					)
					const distanceFromGoogleRoadsLabel = formatTrainerDistanceKm(
						pozycja.odleglosc_drogi_google_km
					)
					const parsedOdlegloscKm = Number(pozycja.odleglosc_km)
					const showAccommodationWarning = Number.isFinite(parsedOdlegloscKm) && parsedOdlegloscKm > 100

					return (
						<div
							key={pozycja._id || `pozycja-${index}`}
							className="rounded-lg border border-gray-200 bg-gray-50 p-4"
						>
							<div className="mb-3 flex items-center justify-between gap-3">
								<span className="inline-flex items-center rounded-full bg-indigo-100 px-2.5 py-0.5 text-xs font-medium text-indigo-800">
									{t('projects.form.positionsSection.itemLabel', {
										defaultValue: 'Pozycja {{number}}',
										number: index + 1,
									})}
								</span>
								<button
									type="button"
									onClick={() => removePozycja(index)}
									className="rounded-md border border-red-200 bg-red-50 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-100"
								>
									{t('projects.form.positionsSection.remove', { defaultValue: 'Usuń pozycję' })}
								</button>
							</div>

							<div className="space-y-4">
								<div>
									<label className="flex flex-col gap-1">
										<span className="text-xs font-medium uppercase tracking-wide text-gray-500">
											{t('projects.form.positionsSection.pozycjaTitle', {
												defaultValue: 'Tytuł pozycji (np. Bajki klasa 3b)',
											})}
										</span>
										<input
											type="text"
											className={inputClassName}
											value={pozycja.value || ''}
											onChange={e => updatePozycja(index, { value: e.target.value }, { recalculate: false })}
											placeholder={t('projects.form.positionsSection.pozycjaTitlePlaceholder', {
												defaultValue: 'np. Bajki klasa 3b',
											})}
										/>
									</label>
									<div className="mt-3 grid max-w-3xl grid-cols-1 gap-3 sm:grid-cols-3">
										<label className="flex flex-col gap-1">
											<span className="text-xs font-medium text-gray-600">
												{t('projects.form.positionsSection.lessonHours', {
													defaultValue: 'Godziny lekcyjne',
												})}
											</span>
											<input
												type="number"
												min="0"
												step="0.01"
												className={inputClassName}
												value={pozycja.ilosc_godzin ?? ''}
												onChange={e => updatePozycja(index, { ilosc_godzin: e.target.value })}
											/>
										</label>
										<label className="flex flex-col gap-1">
											<span className="text-xs font-medium text-gray-600">
												{t('projects.form.positionsSection.startTime', {
													defaultValue: 'Godzina startu',
												})}
											</span>
											<input
												type="time"
												className={inputClassName}
												value={pozycja.start_time || ''}
												onChange={e => updatePozycja(index, { start_time: e.target.value }, { recalculate: false })}
											/>
										</label>
										<label className="flex flex-col gap-1">
											<span className="text-xs font-medium text-gray-600">
												{t('projects.form.positionsSection.endTime', {
													defaultValue: 'Godzina zakończenia',
												})}
											</span>
											<input
												type="time"
												className={inputClassName}
												value={pozycja.end_time || ''}
												onChange={e => updatePozycja(index, { end_time: e.target.value }, { recalculate: false })}
											/>
										</label>
									</div>
								</div>

								<label className="flex flex-col gap-1">
									<span className="text-xs font-medium uppercase tracking-wide text-gray-500">
										{t('projects.form.fields.requiredCertifications')}
									</span>
									<Select
										isMulti
										name={`pozycja-certifications-${index}`}
										options={certificationOptions}
										value={getSelectedCertificationOptions(pozycja)}
										onChange={value => updatePozycja(index, {
											certificates: Array.isArray(value)
												? value.map(option => ({
													certificateId: option.value,
													certificateName: option.label || '',
												}))
												: [],
										}, { recalculate: false })}
										placeholder={t('projects.form.placeholders.requiredCertifications')}
										className="react-select"
										classNamePrefix="react-select"
									/>
								</label>

								<div>
									<div className="mb-2 flex items-center justify-between gap-3">
										<span className="text-xs font-medium uppercase tracking-wide text-gray-500">
											{t('projects.details.sections.trainers')}
										</span>
										<button
											type="button"
											onClick={() => openTrainerModal(index)}
											disabled={loadingTrainerOptions}
											className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
										>
											{pozycja.trainerName
												? t('projects.form.positionsSection.changeTrainer', { defaultValue: 'Zmień trenera' })
												: t('projects.details.actions.addTrainer')}
										</button>
									</div>
									<p className="text-sm font-medium text-gray-900">
										{pozycja.trainerName || t('projects.list.table.unassignedTrainer')}
									</p>
									{pozycja.miasto ? (
										<p className="text-xs text-gray-500">{pozycja.miasto}</p>
									) : null}
									{distanceFromHomeLabel || distanceFromPreviousLabel || distanceFromGoogleRoadsLabel ? (
										<div className="mt-1 space-y-0.5 text-xs text-gray-500">
											{distanceFromHomeLabel ? (
												<p>
													{t('projects.form.positionsSection.distanceFromHome', {
														defaultValue: 'Odległość z domu: {{distance}}',
														distance: distanceFromHomeLabel,
													})}
												</p>
											) : null}
											{distanceFromPreviousLabel ? (
												<p>
													{t('projects.form.positionsSection.distanceFromPreviousPlacowka', {
														defaultValue: 'Odległość z poprzedniej placówki: {{distance}}',
														distance: distanceFromPreviousLabel,
													})}
												</p>
											) : null}
											{distanceFromGoogleRoadsLabel ? (
												<p>
													{t('projects.form.positionsSection.distanceGoogleRoads', {
														defaultValue: 'Odległość drogi Google: {{distance}}',
														distance: distanceFromGoogleRoadsLabel,
													})}
												</p>
											) : null}
										</div>
									) : null}
								</div>

								<div>
									<span className="mb-2 block text-xs font-medium uppercase tracking-wide text-gray-500">
										{t('projects.details.sections.compensation')}
									</span>
									<div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
										<label className="flex flex-col gap-1">
											<span className="text-xs font-medium text-gray-600">
												{t('projects.form.fields.hourlyRateType', {
													defaultValue: 'Typ stawki godzinowej',
												})}
											</span>
											<Select
												name={`pozycja-hourly-rate-type-${index}`}
												options={hourlyRateTypeOptions}
												value={hourlyRateTypeOptions.find(
													option => option.value === normalizeTypStawkiGodzinowej(
														pozycja.typ_stawki_godzinowej
													)
												) || hourlyRateTypeOptions[0]}
												onChange={option => handleHourlyRateTypeChange(index, option)}
												className="react-select"
												classNamePrefix="react-select"
												isSearchable={false}
											/>
										</label>
										<label className="flex flex-col gap-1">
											<span className="text-xs font-medium text-gray-600">
												{t('projects.details.trainerDialog.table.hourlyRateGross')}
											</span>
											<input
												type="number"
												min="0"
												step="0.01"
												className={inputClassName}
												value={pozycja.stawka_godzinowa ?? ''}
												onChange={e => updatePozycja(index, { stawka_godzinowa: e.target.value })}
											/>
										</label>
										<label className="flex flex-col gap-1">
											<span className="text-xs font-medium text-gray-600">
												{t('projects.details.trainerDialog.fields.hoursCount')}
											</span>
											<input
												type="number"
												min="0"
												step="0.01"
												className={inputClassName}
												value={pozycja.ilosc_godzin ?? ''}
												onChange={e => updatePozycja(index, { ilosc_godzin: e.target.value })}
											/>
										</label>
										<div>
											<span className="mb-1 block text-xs font-medium text-gray-600">
												{t('projects.details.trainerDialog.fields.doWyplatyGodziny')}
											</span>
											<div className={readOnlyValueClassName}>{formatMoneyPln(doWyplatyGodziny)}</div>
										</div>
										<label className="flex flex-col gap-1">
											<span className="text-xs font-medium text-gray-600">
												{t('projects.details.trainerDialog.table.distance')}
											</span>
											<input
												type="number"
												min="0"
												step="0.01"
												className={inputClassName}
												value={pozycja.odleglosc_km ?? ''}
												onChange={e => updatePozycja(index, { odleglosc_km: e.target.value })}
											/>
										</label>
										{showAccommodationWarning ? (
											<p className="text-sm font-medium text-red-600 md:col-span-2 xl:col-span-3">
												{t('projects.form.positionsSection.distanceAbove100Warning', {
													defaultValue: 'Odległość powyżej 100km, trener powinien skorzystać z zakwaterowania',
												})}
											</p>
										) : null}
										<label className="flex flex-col gap-1">
											<span className="text-xs font-medium text-gray-600">
												{t('projects.details.trainerDialog.fields.ratePerKm')}
											</span>
											<input
												type="number"
												min="0"
												step="0.01"
												className={inputClassName}
												value={pozycja.stawka_za_kilometr ?? 1}
												onChange={e => updatePozycja(index, { stawka_za_kilometr: e.target.value })}
											/>
										</label>
										<div>
											<span className="mb-1 block text-xs font-medium text-gray-600">
												{t('projects.details.trainerDialog.fields.doWyplatyDojazdy')}
											</span>
											<div className={readOnlyValueClassName}>{formatMoneyPln(doWyplatyDojazdy)}</div>
										</div>
										<div>
											<span className="mb-1 block text-xs font-medium text-gray-600">
												{t('projects.details.trainerDialog.fields.doWyplatyInne')}
											</span>
											<div className={readOnlyValueClassName}>{formatMoneyPln(doWyplatyInne)}</div>
										</div>
										<label className="flex flex-col gap-1">
											<span className="text-xs font-medium text-gray-600">
												{t('projects.details.trainerDialog.fields.doWyplaty')}
											</span>
											<input
												type="number"
												min="0"
												step="0.01"
												className={inputClassName}
												value={pozycja.do_wyplaty ?? ''}
												onChange={e => updatePozycja(index, { do_wyplaty: e.target.value }, { recalculate: false })}
											/>
										</label>
									</div>

									<div className="mt-3">
										<ProjectTrainerExtraCosts
											extraCosts={normalizeTrainerExtraCosts(pozycja.inne_koszty)}
											onAdd={() => updatePozycja(index, {
												inne_koszty: [
													...normalizeTrainerExtraCosts(pozycja.inne_koszty),
													createTrainerExtraCost(),
												],
											})}
											onChange={(costId, field, value) => updatePozycja(index, {
												inne_koszty: normalizeTrainerExtraCosts(pozycja.inne_koszty).map(item => (
													item._id === costId ? { ...item, [field]: value } : item
												)),
											})}
											onRemove={costId => updatePozycja(index, {
												inne_koszty: normalizeTrainerExtraCosts(pozycja.inne_koszty)
													.filter(item => item._id !== costId),
											})}
										/>
									</div>

									<label className="mt-3 flex flex-col gap-1">
										<span className="text-xs font-medium text-gray-600">
											{t('projects.details.trainerDialog.fields.uwagi', { defaultValue: 'Uwagi' })}
										</span>
										<textarea
											rows={3}
											className={inputClassName}
											value={pozycja.uwagi || ''}
											onChange={e => updatePozycja(index, { uwagi: e.target.value }, { recalculate: false })}
											placeholder={t('projects.details.trainerDialog.placeholders.uwagi', {
												defaultValue: 'Uwagi do pozycji...',
											})}
										/>
									</label>
									<button
										type="button"
										onClick={() => addPozycja(index)}
										className="mt-3 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
									>
										{t('projects.form.positionsSection.add', { defaultValue: 'Powiel pozycję' })}
									</button>
								</div>
							</div>
						</div>
					)
				}) : (
					<div>
						<p className="text-sm text-gray-500">
							{t('projects.form.positionsSection.empty', { defaultValue: 'Brak pozycji. Dodaj pierwszą pozycję etapu.' })}
						</p>
						<button
							type="button"
							onClick={() => addPozycja()}
							className="mt-3 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
						>
							{t('projects.form.positionsSection.add', { defaultValue: 'Powiel pozycję' })}
						</button>
					</div>
				)}
			</div>

			<ProjectAddTrainerModal
				isOpen={isAddTrainerModalOpen}
				onClose={() => {
					setIsAddTrainerModalOpen(false)
					setActivePozycjaIndex(null)
				}}
				onSubmit={handleChooseTrainer}
				isSaving={selectingTrainer}
				hasMore={trainerOptionsLimit < sortedTrainerOptions.length}
				onLoadMore={() => setTrainerOptionsLimit(prev => prev + 5)}
				onSearchChange={setTrainerSearchTerm}
				onSort={column => {
					if (trainerSortColumn === column) {
						setTrainerSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'))
						return
					}
					setTrainerSortColumn(column)
					setTrainerSortDirection('asc')
				}}
				searchTerm={trainerSearchTerm}
				sortColumn={trainerSortColumn}
				sortDirection={trainerSortDirection}
				totalCount={sortedTrainerOptions.length}
				trainerOptions={visibleTrainerOptions}
				stepDate={stepDateKey(form.data_rozpoczecia)}
			/>
		</div>
	)
}
